import React, {
    useCallback,
    useState,
} from 'react';
import { buildRange } from '@frontend/jetlend-core/src/utils/arrayUtils';
import { IConnectableInputProps } from '@frontend/jetlend-core/src/ui/inputs/common';
import { connectToField } from '@frontend/jetlend-core/src/ui/inputs/connect';
import PinCodeSlot from './PinCodeSlot';
import styles from './pinCodeInput.module.scss';

export interface IProps extends IConnectableInputProps {
    /**
     * Размер поля ввода
     */
    size: number;
    /**
     * Флаг отключения поля ввода
     */
    disabled?: boolean;
    /**
     * Статус поля ввода
     */
    status?: 'default'|'success'|'error';
    /**
     * Флаг сокрытия вводимых символов
     */
    secure?: boolean;
    /**
     * Флаг одноразового кода
     */
    oneTimeCode?: boolean;
    /**
     * Флаг автофокуса
     */
    autoFocus?: boolean;
}

/**
 * Функция проверяет, что строка состоит только из цифр
 * @param value - значение для проверки
 * @return true, если строка состоит только из цифр
 */
function isNumericValue(value: string): boolean {
    return /^\d+$/.test(value);
}

// Максимальная длина админ-кода.
const ADMIN_CODE_MAX_LENGTH = 8;

/**
 * Компонент для ввода одноразового пароля
 */
function PinCodeInput({
    size,
    disabled,
    status = 'default',
    secure = false,
    oneTimeCode = false,
    autoFocus = false,
    onBlur,
    onFocus,
    onChange,
    value,
    name,
    error,
}: IProps) {
    const [ focus, setFocus ] = useState(false);

    const didBlurred = useCallback(e => {
        setFocus(false);
        onBlur && onBlur(e);
    }, [ onBlur ]);

    const didFocused = useCallback(e => {
        setFocus(true);
        onFocus && onFocus(e);
    }, [ onFocus ]);

    const range = buildRange(0, size - 1);

    const slotsValue = (value as string || '').split('');

    const getSlotValue = (index: number) => index < slotsValue.length
        ? slotsValue[index]
        : '';

    function isCurrentSlot(index: number) {
        return index === Math.max(0, Math.min(value.length, size - 1));
    }

    const didKeyPressed = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Обработка нажатия клавиш enter и backspace
        if (/^backspace|enter$/i.test(e.code)) {
            return;
        }

        // Обработка нажатия клавиш вставки
        if (e.ctrlKey && e.code.toLowerCase() === 'keyv') {
            return;
        }

        // Запрещаем ввод невалидных символов
        if (
            // Валидация средствами браузера (pattern)
            !e.currentTarget.validity.valid ||
            // Валидация средствами JS
            ((e.currentTarget.value + e.key).length > 0 && !isNumericValue(e.key)) ||
            // Ограничение количества введённых символов
            (e.currentTarget.value + e.key).length > size
        ) {
            e.currentTarget.value = value;
            e.preventDefault();
        }
    };

    const didChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!/^[0-9]*$/i.test(e.currentTarget.value)) {
            e.preventDefault();
            return;
        }

        onChange && onChange(e);
    };

    const didPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        // Очищаем от пробелов
        const clipboardData = e.clipboardData.getData('text').replace(/\s/g, '');

        // Запрещаем вставку невалидных символов
        if (
            // Если в буфере обмена не число
            !isNumericValue(clipboardData) ||
            // Если в буфере обмена и поле ввода больше символов, чем нужно
            e.currentTarget.value.length + clipboardData.length > ADMIN_CODE_MAX_LENGTH
        ) {
            e.preventDefault();
        }
    };

    const hasError = error && error.length > 0;

    return (
        <div>
            <div className={styles['input-wrapper']}>
                <div className={styles['slots']}>
                    {range.map(idx => (
                        <PinCodeSlot
                            key={idx}
                            index={idx}
                            disabled={disabled}
                            value={getSlotValue(idx)}
                            current={isCurrentSlot(idx)}
                            secure={secure}
                            focus={focus}
                            status={hasError ? 'error' : status}
                        />
                    )
                    )}
                </div>
                <input
                    className={styles['input']}
                    name={name}
                    autoFocus={autoFocus}
                    type={secure ? 'password' : 'text'}
                    pattern={secure ? undefined : '[0-9]*'}
                    inputMode='numeric'
                    disabled={disabled}
                    autoComplete={oneTimeCode ? 'one-time-code' : 'off'}
                    value={value}
                    onKeyDown={didKeyPressed}
                    onChange={didChanged}
                    onFocus={didFocused}
                    onBlur={didBlurred}
                    onPaste={didPaste}
                />
            </div>
            {hasError && (
                <div className={styles['label__error']}>
                    {error}
                </div>
            )}
        </div>
    );
};

export const PinCodeInputField = connectToField(PinCodeInput);

export default PinCodeInput;